import { createFileRoute, Link, Outlet, useLocation, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { languageOptions, type SupportedLanguage } from "@/i18n/types";
import { checkAdminAccessServer } from "@/server/admin";
import { logError } from "@/lib/structured-log";

interface AdminSearchParams {
  tab?:
    | "workspaces"
    | "campaign"
    | "waitlist"
    | "admins"
    | "monitor"
    | "realtime-jobs"
    | "invitation-codes"
    | "service-requests"
    | "inbox"
    | "outbox";
}

export const Route = createFileRoute("/admin")({
  validateSearch: (search: Record<string, unknown>): AdminSearchParams => ({
    tab:
      (search.tab as
        | "workspaces"
        | "campaign"
        | "waitlist"
        | "admins"
        | "monitor"
        | "realtime-jobs"
        | "invitation-codes"
        | "service-requests"
        | "inbox"
        | "outbox") || undefined,
  }),

  beforeLoad: async () => {
    try {
      const result = await checkAdminAccessServer();
      if (!result.is_admin) {
        logError("admin.access_denied", { reason: "not_admin" });
        throw redirect({ to: "/" });
      }
      return { isAdmin: true };
    } catch (error) {
      // If the API call fails (401, 403, etc.), redirect to home
      logError("admin.access_check_failed", { error: String(error) });
      throw redirect({ to: "/" });
    }
  },

  component: AdminLayout,
});

function AdminLayout() {
  const { t, i18n } = useTranslation("admin");
  const currentLanguage = i18n.language as SupportedLanguage;

  const handleLanguageChange = (languageCode: SupportedLanguage) => {
    i18n.changeLanguage(languageCode);
  };

  const currentLanguageLabel =
    languageOptions.find((l) => l.code === currentLanguage)?.nativeName ?? currentLanguage;

  // Determine active tab from current path using TanStack Router's useLocation
  const { pathname } = useLocation();
  let activeTab:
    | "workspaces"
    | "campaign"
    | "waitlist"
    | "admins"
    | "monitor"
    | "realtime-jobs"
    | "invitation-codes"
    | "service-requests"
    | "inbox"
    | "outbox" = "workspaces";
  if (pathname.includes("/admin/waitlist")) {
    activeTab = "waitlist";
  } else if (pathname.includes("/admin/campaign")) {
    activeTab = "campaign";
  } else if (pathname.includes("/admin/admins")) {
    activeTab = "admins";
  } else if (pathname.includes("/admin/monitor")) {
    activeTab = "monitor";
  } else if (pathname.includes("/admin/realtime-jobs")) {
    activeTab = "realtime-jobs";
  } else if (pathname.includes("/admin/invitation-codes")) {
    activeTab = "invitation-codes";
  } else if (pathname.includes("/admin/service-requests")) {
    activeTab = "service-requests";
  } else if (pathname.includes("/admin/outbox")) {
    activeTab = "outbox";
  } else if (pathname.includes("/admin/inbox")) {
    activeTab = "inbox";
  }

  return (
    <div className="flex h-screen w-full flex-col gap-4 overflow-hidden bg-background px-4 py-4 lg:px-6 lg:py-6">
      {/* Header */}
      <header className="flex shrink-0 items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-semibold tracking-tight">{t("admin.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("admin.description")}</p>
        </div>

        {/* Language Switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger className="inline-flex items-center gap-2 rounded-lg border border-border/70 bg-background px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground">
            <Globe className="h-4 w-4" />
            <span>{currentLanguageLabel}</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-36">
            {languageOptions.map((language) => (
              <DropdownMenuItem
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className="flex items-center justify-between"
              >
                <span>{language.nativeName}</span>
                {currentLanguage === language.code && <Check className="h-4 w-4" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Tab Navigation */}
      <nav className="flex shrink-0 items-center gap-1 border-b">
        <TabLink to="/admin/workspaces" isActive={activeTab === "workspaces"}>
          {t("tabs.workspaces")}
        </TabLink>
        <TabLink to="/admin/campaign" isActive={activeTab === "campaign"}>
          {t("tabs.campaign")}
        </TabLink>
        <TabLink to="/admin/waitlist" isActive={activeTab === "waitlist"}>
          {t("tabs.waitlist")}
        </TabLink>
        <TabLink to="/admin/admins" isActive={activeTab === "admins"}>
          {t("tabs.admins")}
        </TabLink>
        <TabLink to="/admin/monitor" isActive={activeTab === "monitor"}>
          {t("tabs.monitor")}
        </TabLink>
        <TabLink to="/admin/realtime-jobs" isActive={activeTab === "realtime-jobs"}>
          {t("tabs.realtimeJobs")}
        </TabLink>
        <TabLink to="/admin/invitation-codes" isActive={activeTab === "invitation-codes"}>
          {t("tabs.invitationCodes")}
        </TabLink>
        <TabLink to="/admin/service-requests" isActive={activeTab === "service-requests"}>
          {t("tabs.serviceRequests")}
        </TabLink>
        <TabLink to="/admin/inbox" isActive={activeTab === "inbox"}>
          {t("tabs.inbox")}
        </TabLink>
        <TabLink to="/admin/outbox" isActive={activeTab === "outbox"}>
          {t("tabs.outbox")}
        </TabLink>
      </nav>

      {/* Content Area */}
      <section className="min-h-0 flex-1 overflow-hidden rounded-2xl border bg-background">
        <Outlet />
      </section>
    </div>
  );
}

function TabLink({
  to,
  isActive,
  children,
}: {
  to: string;
  isActive: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={[
        "relative px-4 py-3 text-sm font-medium transition-colors",
        "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isActive
          ? "text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary"
          : "text-muted-foreground hover:text-foreground/80",
      ].join(" ")}
    >
      {children}
    </Link>
  );
}
